import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Download, Users, Inbox as InboxIcon, RefreshCw, Send, Mail, FileText, TrendingUp, LayoutDashboard, Image as ImageIcon, Settings as SettingsIcon, Rocket, Radio as RadioIcon, Palette, Mic, Globe } from "lucide-react";
import { Input } from "@/components/ui/input";
import StoriesManager from "@/pages/StoriesManager";
import RadioManager from "@/pages/RadioManager";
import ArtManager from "@/pages/ArtManager";
import PodcastManager from "@/pages/PodcastManager";
import { Inbox } from "@/components/admin/Inbox";
import { MediaLibrary } from "@/components/admin/MediaLibrary";
import { SeoPreview } from "@/components/admin/SeoPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function toCsv(rows, columns) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(r[c.key])).join(",")).join("\n");
  return `${header}\n${body}`;
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function DataTable({ title, columns, rows, testid }) {
  return (
    <div className="border border-white/10" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10">
              {columns.map((c) => (
                <th key={c.key} className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-500 whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-neutral-600">No entries yet.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-b border-white/5 hover:bg-white/5" data-testid={`${title.toLowerCase()}-row-${i}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-neutral-300 align-top max-w-xs">
                    {c.key === "created_at" ? new Date(r[c.key]).toLocaleString() : (r[c.key] || <span className="text-neutral-700">—</span>)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmailComposer({ token, count }) {
  const [subject, setSubject] = useState("");
  const [heading, setHeading] = useState("");
  const [body, setBody] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [personalize, setPersonalize] = useState(true);
  const [sending, setSending] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [stories, setStories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stories`).then((r) => setStories(r.data)).catch(() => {});
  }, []);

  const loadStory = (id) => {
    const s = stories.find((x) => x.id === id);
    if (!s) return;
    const link = `https://vlmagnetwork.com/story/${s.id}`;
    const paras = (s.content || []).slice(0, 4);
    const lines = [...paras, `Read the full feature: <a href="${link}" style="color:#E30613;">${link}</a>`];
    if (s.product_url) lines.push(`Visit: <a href="${s.product_url}" style="color:#E30613;">${s.product_url.replace(/^https?:\/\//, "")}</a>`);
    setSubject(s.title);
    setHeading(s.title);
    setBody(lines.join("\n"));
    setPersonalize(false);
    setResult(null); setError("");
  };

  const send = async (mode) => {
    setError(""); setResult(null);
    if (!subject.trim() || !body.trim()) { setError("Subject and body are required."); return; }
    if (mode === "test" && !testEmail.trim()) { setError("Enter a test email address."); return; }
    if (mode === "all") {
      if (count === 0) { setError("No signups to email yet."); return; }
      if (!window.confirm(`Send this email to all ${count} waitlist signup(s)? This cannot be undone.`)) return;
    }
    setSending(mode);
    try {
      const payload = { subject, heading, body, personalize, ...(mode === "test" ? { test_email: testEmail } : {}) };
      const { data } = await axios.post(`${API}/admin/broadcast`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Send failed. Try again.");
    } finally {
      setSending("");
    }
  };

  return (
    <div className="max-w-3xl" data-testid="email-composer">
      <p className="text-neutral-400 text-sm mb-6">Compose a launch announcement. Send a test to yourself first, then blast all {count} waitlist signup(s).</p>
      <div className="space-y-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Start from a story (optional)</p>
          <select data-testid="email-load-story" defaultValue="" onChange={(e) => loadStory(e.target.value)} className="w-full h-12 rounded-none bg-black/40 border border-white/25 focus:border-[#E30613] focus:outline-none text-white px-3 text-sm">
            <option value="" disabled>Pick a published story to prefill subject, heading and body…</option>
            {stories.map((s) => <option key={s.id} value={s.id}>{s.network} · {s.title}</option>)}
          </select>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Subject</p>
          <Input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="VLMAG is now live" className="h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Heading (optional)</p>
          <Input data-testid="email-heading" value={heading} onChange={(e) => setHeading(e.target.value)} placeholder="We're live." className="h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Body (one paragraph per line)</p>
          <textarea data-testid="email-body" value={body} onChange={(e) => setBody(e.target.value)} rows={7} placeholder={"The wait is over. VLMAG is now live.\n\nExplore the network and the first stories from the culture."} className="w-full rounded-none bg-black/40 border border-white/25 focus:border-[#E30613] focus:outline-none text-white placeholder:text-neutral-600 p-4 text-sm leading-relaxed" />
        </div>

        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="email-personalize-toggle">
          <input type="checkbox" checked={personalize} onChange={(e) => setPersonalize(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#E30613]" />
          <span className="text-xs text-neutral-300">
            <span className="uppercase tracking-[0.15em] font-bold">Personalize per network</span>
            <span className="block text-neutral-500 mt-1 normal-case tracking-normal">Adds a Music / Style / Culture intro line based on each subscriber's chosen network.</span>
          </span>
        </label>

        {error && <p data-testid="email-error" className="text-[#E30613] text-sm">{error}</p>}
        {result && (          <div data-testid="email-result" className="border border-[#E30613] bg-[#E30613]/10 p-4 text-sm">
            {result.mode === "test"
              ? "Test email sent. Check your inbox."
              : `Blast complete — sent ${result.sent} of ${result.total}${result.failed ? `, ${result.failed} failed` : ""}.`}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-white/10 mt-2">
          <div className="flex-1 flex gap-2">
            <Input data-testid="email-test-address" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your@email.com" className="h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600" />
            <button onClick={() => send("test")} data-testid="email-send-test" disabled={!!sending} className="whitespace-nowrap px-5 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors disabled:opacity-60 inline-flex items-center gap-2">
              {sending === "test" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send Test
            </button>
          </div>
          <button onClick={() => send("all")} data-testid="email-send-all" disabled={!!sending} className="px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {sending === "all" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send To All ({count})
          </button>
        </div>
      </div>
    </div>
  );
}

function RecapPanel({ token, count }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const loadPreview = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/admin/recap/preview`, { headers });
      setItems(data.items || []);
      setKind(data.kind || "");
    } catch (err) {
      setError("Couldn't load recap preview.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const send = async (mode) => {
    setError(""); setResult(null);
    if (mode === "test" && !testEmail.trim()) { setError("Enter a test email address."); return; }
    if (mode === "all") {
      if (count === 0) { setError("No signups to email yet."); return; }
      if (!window.confirm(`Send the weekly recap to all ${count} waitlist signup(s) now?`)) return;
    }
    setSending(mode);
    try {
      const payload = mode === "test" ? { test_email: testEmail } : {};
      const { data } = await axios.post(`${API}/admin/recap/send`, payload, { headers });
      setResult(data);
      if (mode === "all") loadPreview();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Send failed. Try again.");
    } finally {
      setSending("");
    }
  };

  const label = kind === "trending" ? "Most shared since the last recap"
    : kind === "alltime" ? "Most shared all-time (no new shares since last recap)"
    : "Freshest features (no shares tracked yet)";

  return (
    <div className="max-w-3xl" data-testid="recap-panel">
      <div className="flex items-center gap-2 border border-[#E30613]/40 bg-[#E30613]/10 text-neutral-200 text-xs px-4 py-3 mb-6">
        <TrendingUp size={14} className="text-[#E30613]" /> Auto-sends to the whole waitlist every 7 days. Preview below is exactly what goes out.
      </div>
      <p className="text-neutral-400 text-sm mb-4">{label} — top {items.length || 0} feature(s).</p>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[#E30613]" size={26} /></div>
      ) : items.length === 0 ? (
        <p className="text-neutral-600 py-10" data-testid="recap-empty">No published features to send yet.</p>
      ) : (
        <div className="space-y-3 mb-8" data-testid="recap-preview-list">
          {items.map((it, i) => (
            <div key={it.id} className="flex items-center gap-4 border border-white/10 p-3" data-testid={`recap-item-${i}`}>
              <span className="font-display text-2xl text-neutral-600 w-7 shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold truncate">{it.title}</p>
                <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-500 mt-1">{it.network}</p>
              </div>
              <span className="text-[#E30613] text-sm font-bold tabular-nums shrink-0">
                {it.delta > 0 ? `+${it.delta}` : it.total} share{(it.delta > 0 ? it.delta : it.total) === 1 ? "" : "s"}
              </span>
            </div>
          ))}
        </div>
      )}

      {error && <p data-testid="recap-error" className="text-[#E30613] text-sm mb-3">{error}</p>}
      {result && (
        <div data-testid="recap-result" className="border border-[#E30613] bg-[#E30613]/10 p-4 text-sm mb-3">
          {result.mode === "test" ? "Test recap sent. Check your inbox." : `Recap sent — ${result.sent} of ${result.total}${result.failed ? `, ${result.failed} failed` : ""}.`}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
        <div className="flex-1 flex gap-2">
          <Input data-testid="recap-test-address" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your@email.com" className="h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600" />
          <button onClick={() => send("test")} data-testid="recap-send-test" disabled={!!sending || items.length === 0} className="whitespace-nowrap px-5 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors disabled:opacity-60 inline-flex items-center gap-2">
            {sending === "test" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send Test
          </button>
        </div>
        <button onClick={() => send("all")} data-testid="recap-send-all" disabled={!!sending || items.length === 0} className="px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {sending === "all" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send Recap Now ({count})
        </button>
      </div>
    </div>
  );
}


function LaunchPanel({ count }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/launch-email`); setStatus(data); }
    catch (e) { setError("Couldn't load launch status."); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const send = async (mode) => {
    setError(""); setResult(null);
    if (mode === "test" && !testEmail.trim()) { setError("Enter a test email address."); return; }
    if (mode === "all") {
      if (count === 0) { setError("No signups to email yet."); return; }
      if (!window.confirm(`Send the launch announcement to all ${count} waitlist signup(s) NOW? This is the real launch blast and cannot be undone.`)) return;
    }
    setSending(mode);
    try {
      const payload = mode === "test" ? { test_email: testEmail } : {};
      const { data } = await axios.post(`${API}/admin/launch-email`, payload);
      setResult(data);
      if (mode === "all") load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Send failed. Try again.");
    } finally { setSending(""); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E30613]" size={22} /></div>;

  const launchMs = status?.launch_ts ? new Date(status.launch_ts).getTime() : 0;
  const diff = Math.max(0, launchMs - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const sent = status?.sent;

  return (
    <div className="max-w-3xl" data-testid="launch-panel">
      <div className={`flex items-center gap-2 border text-xs px-4 py-3 mb-6 ${sent ? "border-green-500/40 bg-green-500/10 text-green-300" : "border-[#E30613]/40 bg-[#E30613]/10 text-neutral-200"}`} data-testid="launch-status">
        <Rocket size={14} className={sent ? "text-green-400" : "text-[#E30613]"} />
        {sent
          ? `Launch email already sent${status?.sent_at ? ` on ${new Date(status.sent_at).toLocaleString()}` : ""}${status?.result ? ` — ${status.result.sent}/${status.result.total} delivered` : ""}.`
          : "Armed. Fires automatically to the whole waitlist the moment the countdown hits zero."}
      </div>

      {!sent && (
        <div className="border border-white/10 p-6 md:p-7 mb-6" data-testid="launch-countdown">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E30613] mb-4">Auto-send in</p>
          <div className="grid grid-cols-4 gap-3 max-w-md">
            {[["Days", d], ["Hrs", h], ["Min", m], ["Sec", s]].map(([lbl, val]) => (
              <div key={lbl} className="border border-white/10 py-4 text-center">
                <p className="font-display text-4xl text-white tabular-nums leading-none">{String(val).padStart(2, "0")}</p>
                <p className="text-[9px] uppercase tracking-[0.25em] font-bold text-neutral-500 mt-2">{lbl}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-neutral-500 mt-4">Target: {new Date(launchMs).toLocaleString()} · September 26, 2026 · 12:00 PM ET</p>
        </div>
      )}

      <p className="text-neutral-400 text-sm mb-4">Preview the "We're Live" announcement by sending yourself a test, or fire it manually to all {count} signup(s) as a backup.</p>

      {error && <p data-testid="launch-error" className="text-[#E30613] text-sm mb-3">{error}</p>}
      {result && (
        <div data-testid="launch-result" className="border border-[#E30613] bg-[#E30613]/10 p-4 text-sm mb-3">
          {result.mode === "test" ? "Test launch email sent. Check your inbox." : `Launch blast sent — ${result.sent} of ${result.total}${result.failed ? `, ${result.failed} failed` : ""}.`}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
        <div className="flex-1 flex gap-2">
          <Input data-testid="launch-test-address" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your@email.com" className="h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600" />
          <button onClick={() => send("test")} data-testid="launch-send-test" disabled={!!sending} className="whitespace-nowrap px-5 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors disabled:opacity-60 inline-flex items-center gap-2">
            {sending === "test" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send Test
          </button>
        </div>
        <button onClick={() => send("all")} data-testid="launch-send-all" disabled={!!sending} className="px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {sending === "all" ? <Loader2 className="animate-spin" size={14} /> : <Rocket size={14} />} {sent ? "Re-send To All" : "Send Now"} ({count})
        </button>
      </div>
    </div>
  );
}


function SettingsPanel({ user }) {
  const isAdmin = (user?.role || "admin") === "admin";
  const [enabled, setEnabled] = useState(false);
  const [mediaKit, setMediaKit] = useState("");
  const [savedKit, setSavedKit] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [team, setTeam] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/admin/settings`),
        axios.get(`${API}/admin/team`),
      ]);
      setEnabled(!!s.data.autoresponder_enabled);
      setMediaKit(s.data.media_kit_url || ""); setSavedKit(s.data.media_kit_url || "");
      setTeam(t.data.members || []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setSaving("toggle");
    try { const { data } = await axios.post(`${API}/admin/settings`, { autoresponder_enabled: !enabled }); setEnabled(!!data.autoresponder_enabled); }
    catch (e) { /* noop */ } finally { setSaving(""); }
  };
  const saveKit = async () => {
    setSaving("kit");
    try { const { data } = await axios.post(`${API}/admin/settings`, { media_kit_url: mediaKit }); setSavedKit(data.media_kit_url || ""); setMediaKit(data.media_kit_url || ""); }
    catch (e) { /* noop */ } finally { setSaving(""); }
  };
  const setRole = async (email, role) => {
    try { await axios.post(`${API}/admin/team/role`, { email, role }); setTeam((t) => t.map((m) => m.email === email ? { ...m, role } : m)); }
    catch (e) { /* noop */ }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E30613]" size={22} /></div>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-panel">
      {/* Auto-responder */}
      <div className="border border-white/10 p-6 md:p-7">
        <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E30613] mb-3">AI Auto-Responder</p>
        <h3 className="font-display uppercase text-2xl tracking-tight text-white mb-3">Automatic Replies</h3>
        <p className="text-sm text-neutral-400 leading-relaxed mb-6">
          ON: every new submission gets an instant, personalized reply. OFF: the AI drafts a reply in your Inbox to review and send with one click.
        </p>
        <button onClick={toggle} data-testid="autoresponder-toggle" disabled={!isAdmin || saving === "toggle"}
          className={`inline-flex items-center gap-3 px-5 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors disabled:opacity-50 ${enabled ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-300 hover:text-white"}`}>
          {saving === "toggle" ? <Loader2 className="animate-spin" size={14} /> : <span className={`w-2.5 h-2.5 rounded-full ${enabled ? "bg-white" : "bg-neutral-600"}`} />}
          Auto-send is {enabled ? "ON" : "OFF"}
        </button>
        {!isAdmin && <p className="text-[10px] text-neutral-600 mt-2">Editors can view but not change this.</p>}
      </div>

      {/* Media kit */}
      <div className="border border-white/10 p-6 md:p-7">
        <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E30613] mb-3">Advertising / Collab</p>
        <h3 className="font-display uppercase text-2xl tracking-tight text-white mb-3">Media Kit Link</h3>
        <p className="text-sm text-neutral-400 leading-relaxed mb-4">
          Paste your media kit URL. The AI responder shares it automatically with every advertising & collaboration inquiry.
        </p>
        <div className="flex gap-2">
          <Input data-testid="media-kit-input" value={mediaKit} onChange={(e) => setMediaKit(e.target.value)} disabled={!isAdmin}
            placeholder="https://vlmagnetwork.com/media-kit.pdf"
            className="h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600" />
          <button onClick={saveKit} data-testid="media-kit-save" disabled={!isAdmin || saving === "kit" || mediaKit === savedKit}
            className="shrink-0 px-5 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-50">
            {saving === "kit" ? <Loader2 className="animate-spin" size={14} /> : "Save"}
          </button>
        </div>
        {savedKit && <p className="text-[10px] text-neutral-500 mt-2" data-testid="media-kit-current">Currently sharing: {savedKit}</p>}
      </div>

      {/* Team roles */}
      <div className="border border-white/10 p-6 md:p-7">
        <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E30613] mb-3">Team</p>
        <h3 className="font-display uppercase text-2xl tracking-tight text-white mb-2">Roles & Permissions</h3>
        <p className="text-sm text-neutral-400 leading-relaxed mb-6">
          <b className="text-white">Admins</b> can publish, broadcast, manage settings & roles. <b className="text-white">Editors</b> can create/edit drafts and reply in the Inbox, but can't publish or broadcast.
        </p>
        <div className="divide-y divide-white/5" data-testid="team-list">
          {team.map((m) => (
            <div key={m.email} className="flex items-center justify-between gap-3 py-3" data-testid={`team-member-${m.email}`}>
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{m.name || m.email.split("@")[0]}{m.email === user?.email ? " (you)" : ""}</p>
                <p className="text-[11px] text-neutral-600 truncate">{m.email}{!m.active && " · not signed in yet"}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                {["admin", "editor"].map((r) => (
                  <button key={r} data-testid={`role-${m.email}-${r}`} onClick={() => isAdmin && setRole(m.email, r)} disabled={!isAdmin}
                    className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border transition-colors disabled:opacity-60 ${m.role === r ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/20 text-neutral-500 hover:text-white"}`}>
                    {r}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        {!isAdmin && <p className="text-[10px] text-neutral-600 mt-4">Only admins can change roles.</p>}
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-white/10 p-6" data-testid={testid}>
      <p className="font-display text-5xl text-white tabular-nums leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500 mt-3">{label}</p>
    </div>
  );
}

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "content", label: "Content", icon: FileText },
  { key: "radio", label: "VLMAG Radio", icon: RadioIcon },
  { key: "art", label: "VLMAG Art", icon: Palette },
  { key: "podcast", label: "VLMAG Podcast", icon: Mic },
  { key: "media", label: "Media Library", icon: ImageIcon },
  { key: "seo", label: "Search Preview", icon: Globe },
  { key: "inbox", label: "Smart Inbox", icon: InboxIcon },
  { key: "waitlist", label: "Waitlist", icon: Users },
  { key: "broadcast", label: "Broadcast", icon: Mail },
  { key: "launch", label: "Launch Email", icon: Rocket },
  { key: "recap", label: "Weekly Recap", icon: TrendingUp },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("overview");
  const [waitlist, setWaitlist] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Auth gate: handle OAuth callback hash, else verify session
  useEffect(() => {
    (async () => {
      const hash = window.location.hash || "";
      const m = hash.match(/session_id=([^&]+)/);
      if (m) {
        try {
          const { data } = await axios.post(`${API}/auth/session`, { session_id: decodeURIComponent(m[1]) });
          window.history.replaceState(null, "", window.location.pathname);
          setUser(data); setAuthed(true); return;
        } catch (e) {
          navigate("/login?error=denied"); return;
        }
      }
      try { const { data } = await axios.get(`${API}/auth/me`); setUser(data); setAuthed(true); }
      catch (e) { navigate("/login"); }
    })();
  }, [navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [w, s] = await Promise.all([
        axios.get(`${API}/waitlist`),
        axios.get(`${API}/submissions`),
      ]);
      setWaitlist(w.data); setSubmissions(s.data);
    } catch (e) { /* auth handled above */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (authed) loadData(); }, [authed, loadData]);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* noop */ }
    navigate("/login");
  };

  if (authed === null) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#E30613]" size={30} /></div>;
  }

  const isAdmin = (user?.role || "admin") === "admin";
  const ADMIN_ONLY = new Set(["broadcast", "launch", "recap", "settings", "waitlist"]);
  const navItems = NAV.filter((n) => isAdmin || !ADMIN_ONLY.has(n.key));
  const wlCols = [
    { key: "name", label: "Name" }, { key: "email", label: "Email" },
    { key: "network", label: "Network" }, { key: "created_at", label: "Joined" },
  ];
  const newCount = submissions.filter((s) => (s.status || "new") === "new").length;

  const SectionTitle = ({ children, sub }) => (
    <div className="mb-8">
      <h1 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-none">{children}</h1>
      {sub && <p className="text-neutral-500 text-sm mt-2">{sub}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-white/10 sticky top-0 h-screen">
        <div className="px-6 h-[68px] flex items-center border-b border-white/10">
          <div className="font-display text-2xl tracking-tight leading-none">
            <span className="text-[#E30613]">VL</span><span>MAG</span>
          </div>
        </div>
        <nav className="flex-1 py-4">
          {navItems.map((n) => {
            const Icon = n.icon;
            const active = tab === n.key;
            return (
              <button key={n.key} data-testid={`nav-${n.key}`} onClick={() => setTab(n.key)}
                className={`w-full flex items-center gap-3 px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors relative ${active ? "text-white bg-white/[0.05]" : "text-neutral-500 hover:text-white"}`}>
                {active && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#E30613]" />}
                <Icon size={15} /> {n.label}
                {n.key === "inbox" && newCount > 0 && <span className="ml-auto text-[9px] bg-[#E30613] text-white px-1.5 py-0.5 rounded-full">{newCount}</span>}
              </button>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-4">
          {user && (
            <div className="flex items-center gap-3 mb-3 px-2">
              {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-[#E30613]/20 flex items-center justify-center text-xs font-bold text-[#E30613]">{(user.name || user.email || "?")[0].toUpperCase()}</div>}
              <div className="min-w-0"><p className="text-xs text-white truncate" data-testid="admin-user-name">{user.name || "Team"}</p><p className="text-[10px] text-neutral-600 truncate">{user.email}</p></div>
            </div>
          )}
          <button onClick={logout} data-testid="admin-logout" className="w-full inline-flex items-center gap-2 px-2 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-20 bg-black border-b border-white/10">
        <div className="flex items-center justify-between px-5 h-[60px]">
          <div className="font-display text-xl"><span className="text-[#E30613]">VL</span>MAG</div>
          <button onClick={logout} data-testid="admin-logout-mobile" className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400"><LogOut size={16} /></button>
        </div>
        <div className="flex overflow-x-auto no-scrollbar border-t border-white/5">
          {navItems.map((n) => (
            <button key={n.key} onClick={() => setTab(n.key)} data-testid={`nav-m-${n.key}`}
              className={`whitespace-nowrap px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-bold ${tab === n.key ? "text-[#E30613]" : "text-neutral-500"}`}>{n.label}</button>
          ))}
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 px-5 md:px-10 py-10 pt-[120px] md:pt-10">
        {tab === "overview" && (
          <div data-testid="overview-panel">
            <SectionTitle sub={`Welcome back${user?.name ? `, ${user.name.split(" ")[0]}` : ""}. Here's your operation at a glance.`}>Overview</SectionTitle>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <StatCard label="Waitlist Signups" value={waitlist.length} testid="stat-waitlist" />
              <StatCard label="Total Submissions" value={submissions.length} testid="stat-submissions" />
              <StatCard label="Needs Reply" value={newCount} testid="stat-new" />
              <StatCard label="Replied" value={submissions.filter((s) => s.status === "replied").length} testid="stat-replied" />
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setTab("content")} className="px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors">New Article</button>
              <button onClick={() => setTab("inbox")} className="px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-white hover:border-[#E30613] transition-colors">Open Inbox ({newCount})</button>
            </div>
          </div>
        )}

        {tab === "content" && (<div><SectionTitle sub="Create, edit and publish Music & Lifestyle features. Set status to Draft, Review or Published.">Content Manager</SectionTitle><StoriesManager token="" role={user?.role} /></div>)}

        {tab === "radio" && (<div><SectionTitle sub="Curate VLMAG Radio — upload exclusive tracks & beats, feature artists, and review submissions from creatives.">VLMAG Radio</SectionTitle><RadioManager role={user?.role} /></div>)}

        {tab === "art" && (<div><SectionTitle sub="Curate the VLMAG Art gallery — add exclusive pieces, feature highlights, manage sales, and review artist submissions.">VLMAG Art</SectionTitle><ArtManager role={user?.role} /></div>)}

        {tab === "podcast" && (<div><SectionTitle sub="BEYOND THE VISUAL — manage episodes, see who's waiting to listen, and flip the show from Coming Soon to live.">VLMAG Podcast</SectionTitle><PodcastManager role={user?.role} /></div>)}

        {tab === "media" && (<div><SectionTitle sub="Upload and manage high-quality images and video.">Media Library</SectionTitle><MediaLibrary /></div>)}

        {tab === "seo" && (<div><SectionTitle sub="See exactly how each story appears in Google results and when shared on X, Facebook, iMessage or Instagram — and fix anything that's cut off.">Search Preview</SectionTitle><SeoPreview /></div>)}

        {tab === "inbox" && (<div><SectionTitle sub="Every talent, collab and team submission — with a personalized AI reply ready to review and send.">Smart Inbox</SectionTitle><Inbox /></div>)}

        {tab === "waitlist" && isAdmin && (
          <div>
            <div className="flex items-center justify-between gap-4 mb-8">
              <SectionTitle sub="Everyone waiting for the September 26 launch.">Waitlist</SectionTitle>
              <div className="flex gap-2 shrink-0">
                <button onClick={loadData} data-testid="admin-refresh" className="inline-flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/20 text-neutral-400 hover:text-white transition-colors"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
                <button onClick={() => download("vlmag-waitlist.csv", toCsv(waitlist, wlCols))} data-testid="admin-export-csv" className="inline-flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold bg-white text-black hover:bg-neutral-200 transition-colors"><Download size={14} /> Export CSV</button>
              </div>
            </div>
            {loading ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#E30613]" size={28} /></div> : <DataTable title="Waitlist" columns={wlCols} rows={waitlist} testid="admin-table" />}
          </div>
        )}

        {tab === "broadcast" && isAdmin && (<div><SectionTitle sub="Send a launch announcement to the whole waitlist.">Broadcast</SectionTitle><EmailComposer token="" count={waitlist.length} /></div>)}

        {tab === "launch" && isAdmin && (<div><SectionTitle sub="Auto-emails the entire waitlist the moment the countdown hits zero. Send a test or fire it manually as a backup.">Launch Email</SectionTitle><LaunchPanel count={waitlist.length} /></div>)}

        {tab === "recap" && isAdmin && (<div><SectionTitle sub="The most-shared features, auto-emailed weekly.">Weekly Recap</SectionTitle><RecapPanel token="" count={waitlist.length} /></div>)}

        {tab === "settings" && isAdmin && (<div><SectionTitle sub="Control how the VLMAG operation runs.">Settings</SectionTitle><SettingsPanel user={user} /></div>)}
      </main>
    </div>
  );
}
